import React, { useEffect } from "react";
import { Drawer, Form, Input, Switch, Button, Space, message, Card } from "antd";
import { UIAssetFilterDTO, UIAssetFilterGroupDTO, UIAssetFilterGroupEditDTO } from "../../backendapi/requests";
import { useUiAssetFilterServicePutBackendv2Uiassetfiltergroup } from "../../backendapi/queries";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

interface EditUIAssetFilterGroupDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  filterGroup: UIAssetFilterGroupDTO;
}

interface UIAssetFilterGroupFormData {
  title: string;
  clientId: number;
  enabledForAssets: boolean;
  enabledForTemplates: boolean;
  enabledForConfiguration: boolean;
  filters: UIAssetFilterDTO[];
}

const EditUIAssetFilterGroup: React.FC<EditUIAssetFilterGroupDrawerProps> = ({ visible, onClose, onSuccess, filterGroup }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const { mutateAsync: updateFilterGroup } = useUiAssetFilterServicePutBackendv2Uiassetfiltergroup();

  useEffect(() => {
    if (visible && filterGroup) {
      form.setFieldsValue({
        title: filterGroup.title,
        enabledForAssets: filterGroup.enabledForAssets,
        enabledForTemplates: filterGroup.enabledForTemplates,
        enabledForConfiguration: filterGroup.enabledForConfiguration,
        filters: filterGroup.filters,
      });
    }
  }, [visible, filterGroup, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue(true) as UIAssetFilterGroupEditDTO;
      values.clientId = filterGroup.clientId;
      await updateFilterGroup({ key: filterGroup.id, requestBody: values });

      message.success("Filter group updated successfully");
      onSuccess();
      onClose();
    } catch (error) {
      message.error("Failed to update filter group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Edit Filter Group"
      placement="right"
      size={500}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            Update
          </Button>
        </Space>
      }>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: "Please enter a title" },
            { min: 3, message: "Title must be at least 3 characters" },
          ]}>
          <Input placeholder="Enter filter group title" />
        </Form.Item>

        <Form.Item label="Enabled for Assets" name="enabledForAssets" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Enabled for Templates" name="enabledForTemplates" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Enabled for Configuration" name="enabledForConfiguration" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Filters" name="filters">
          <Form.List name="filters">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={`Filter ${index + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                        Remove
                      </Button>
                    }>
                    <Form.Item
                      {...field}
                      name={[field.name, "title"]}
                      label="Title"
                      rules={[
                        { required: true, message: "Please enter a title" },
                        { min: 3, message: "Title must be at least 3 characters" },
                      ]}>
                      <Input placeholder="Enter filter title" />
                    </Form.Item>

                    <Form.Item {...field} name={[field.name, "filter"]} label="Filter Expression" rules={[{ required: true, message: "Please enter a filter" }]}>
                      <Input.TextArea placeholder="Enter filter expression" rows={3} />
                    </Form.Item>

                    <Form.Item {...field} name={[field.name, "id"]} hidden>
                      <Input />
                    </Form.Item>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Filter
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditUIAssetFilterGroup;
